<script setup lang="ts">
import { computed, inject } from "vue";
import {
  useAuthStore,
  useAwardStore,
  useCandidatesStore,
  useTicketsStore,
} from "@/stores";
import { format, isBefore, isToday, parseISO, set, sub } from "date-fns";
import { validTicketHolder } from "@/stores/tickets";
import VerifyOwnershipDialog from "@/components/CandidateRegistration/VerifyOwnershipDialog.vue";
import useSWRV from "swrv";
import useCandidate from "@/composables/use-candidate";
import useAwards from "@/composables/use-awards";
import GenericDialog from "@/components/GenericDialog.vue";
import { formatPhone } from "@/util/phone";

const $dialog: any = inject("$dialog");

const authStore = useAuthStore();
const awardStore = useAwardStore();
const candidatesStore = useCandidatesStore();
const ticketsStore = useTicketsStore();

const { settings: awardSettings, afterCutoff } = useAwards();
const {
  status: candidateStatus,
  candidate,
  ceremonyNight,
  claimedNomination,
  instances,
} = useCandidate();

useSWRV("/tickets", ticketsStore.getAllTickets, {
  refreshInterval: 5 * 60_000,
  shouldRetryOnError: false,
});

const loading = computed(() => {
  return !awardSettings.value || !candidate.value;
});

const today = set(new Date(), {
  date: new Date().getDate(),
  hours: 0,
  minutes: 0,
});

const { data: announcementItems } = useSWRV(
  "announcement-items",
  () => awardStore.getAnnouncements(),
  {
    refreshInterval: 60_000 * 5,
    shouldRetryOnError: false,
  }
);

const { data: calendarItems } = useSWRV(
  "calendar-items",
  () => awardStore.getCalendarItems(),
  {
    refreshInterval: 60_000 * 5,
    shouldRetryOnError: false,
  }
);

const phones = computed(() => {
  if (!candidate.value) {
    return [];
  }
  const phones: string[] = [];
  const { primaryPhone = "", locations = [] } = candidate.value;
  if (primaryPhone) {
    phones.push(primaryPhone);
  }
  for (const location of locations) {
    if (location.verifiedYears?.length && location.phone) {
      phones.push(location.phone);
    }
  }
  return [...new Set(phones)];
});

const websiteDomain = computed(() => {
  if (!candidate.value?.website) {
    return "";
  }
  try {
    let _website = candidate.value.website;
    if (!/^http/.test(_website)) {
      _website = `http://${_website}`;
    }
    const url = new URL(_website);
    return url.hostname.replace(/^www\./, "");
  } catch (error) {
    return "";
  }
});

const domainsMatch = computed(() => {
  const emailDomainMatch = authStore.user?.email?.match(/^.*@(.*)$/);
  let emailDomain: string = "";
  if (emailDomainMatch) {
    emailDomain = emailDomainMatch[1];
  }
  if (!emailDomain || !websiteDomain.value) {
    return false;
  }
  return (
    emailDomain.toLocaleLowerCase() === websiteDomain.value.toLocaleLowerCase()
  );
});

function verifyOwnership() {
  if (!candidate.value || !phones.value.length) {
    return;
  }
  $dialog.addDialog({
    component: VerifyOwnershipDialog,
    props: {
      candidate: candidate.value,
      phones: [...new Set(phones.value)],
      email: authStore.user.email,
      domainsMatch: domainsMatch.value,
      formData: {
        email: { value: authStore.user.email },
        phone: { value: formatPhone(candidate.value.primaryPhone) },
        firstName: { value: authStore.user.firstName },
        lastName: { value: authStore.user.lastName },
        title: { value: authStore.user.title },
        domain: "",
      },
      onVerified: ({ manual }: { manual: boolean }) => {
        if (!manual && candidate.value) {
          candidatesStore
            .verifyOwnership(candidate.value.id)
            .then((success) => {
              if (success) {
                authStore.user.candidateVerified = true;
                $dialog.addDialog({
                  component: GenericDialog,
                  props: {
                    title: "Verified!",
                    message:
                      "Thanks for verifying. You now have full access to the Candidate Dashboard.",
                    showCancelButton: false,
                  },
                });
              }
            });
        } else if (manual) {
          $dialog.addDialog({
            component: GenericDialog,
            props: {
              title: "Thanks!",
              message: "We'll be in contact with you soon.",
              showCancelButton: false,
            },
          });
        }
      },
    },
  });
}
</script>

<template>
  <div class="home">
    <LoadingOverlay :is-loading="loading" />
    <div v-if="!loading" class="pb-4 flex flex-col gap-y-2">
      <div class="flex w-full flex-wrap gap-2">
        <div v-if="!authStore.user.candidateVerified">
          <Card
            class="flex h-[12rem] w-full max-w-md flex-col gap-y-2 justify-between"
            v-if="instances.some((i) => i.accepted) && phones.length"
            title=""
          >
            <p
              class="rounded border border-yellow-300 bg-yellow-100 p-2 font-medium"
            >
              To gain full access to the Candidate Dashboard, please verify that
              you own or represent
              <span class="italic">{{ candidate?.name }}.</span>
            </p>
            <div class="flex justify-center">
              <Button class="w-40" @click="verifyOwnership"
                >Verify Ownership</Button
              >
            </div>
          </Card>
          <Card
            v-else
            title="Review in Progress"
            class="flex h-[12rem] w-full max-w-md flex-col gap-y-2"
          >
            <p
              class="rounded border border-yellow-300 bg-yellow-100 p-2 font-medium"
            >
              Once we have reviewed and accepted your registration, you will
              have full access to the Candidate Dashboard.
            </p>
          </Card>
        </div>
        <Card
          class="flex h-[12rem] w-full max-w-md flex-col items-center gap-y-2 justify-between"
          title=""
          v-if="
            ceremonyNight &&
            candidate?.tickets.length &&
            !awardStore.awardsCeremonyOver &&
            candidate?.tickets.some((t) => !validTicketHolder(t))
          "
        >
          <p class="font-medium text-red-400">
            Please assign your {{ awardStore.awardYear }} Awards Ceremony
            tickets to your guests who are attending. They must be assigned by
            <span class="font-medium">{{
              format(sub(parseISO(ceremonyNight.date), { days: 3 }), "MMM. do")
            }}</span>
          </p>
          <Button @click="() => $router.push('/awards-ceremony')"
            >Assign Tickets</Button
          >
        </Card>
        <Card
          class="flex w-full max-w-md flex-col justify-between items-center gap-y-2 h-[12rem]"
          title=""
          v-if="ticketsStore.upgradeableTicketCount"
        >
          <p class="font-medium">
            You can upgrade your {{ awardStore.awardYear }} Awards Ceremony
            General Entry tickets to V.I.P. tickets!
          </p>
          <Button @click="() => $router.push('upgrade-to-vip')"
            >Upgrade to V.I.P.!</Button
          >
        </Card>
      </div>
      <div class="flex w-full flex-wrap gap-x-2 gap-y-2">
        <div class="flex flex-col gap-y-2">
          <Card
            class="flex h-[20rem] w-full max-w-md flex-col justify-between gap-y-2"
          >
            <div>
              <h2>Welcome!</h2>
              <p v-html="awardStore.welcomeText ?? ''" />
            </div>
            <div
              v-if="
                candidateStatus.isNominee && !claimedNomination && !afterCutoff
              "
              class="flex justify-center"
            >
              <Button @click="() => $router.push({ name: 'claim-nomination' })"
                >Claim Your Nomination!</Button
              >
            </div>
          </Card>
          <Card
            class="w-full max-w-md flex flex-col grow"
            v-if="announcementItems?.length"
          >
            <h2>Announcements</h2>
            <ul>
              <li
                v-for="(item, index) in announcementItems"
                :key="index"
                class="py-2"
              >
                <p class="font-bold">
                  {{ format(item.date, "MMM d, yyyy") }}
                </p>
                <p v-html="item.text"></p>
              </li>
            </ul>
          </Card>
        </div>
        <Card class="w-full max-w-md" v-if="calendarItems?.length">
          <h2>Calendar</h2>
          <ul>
            <li
              v-for="(item, index) in calendarItems"
              :key="index"
              class="py-2"
            >
              <div
                :class="{
                  italic: isToday(item.date),
                  'text-green-600': isToday(item.date),
                  'text-gray-400': isBefore(item.date, today),
                }"
              >
                <p class="font-bold">
                  {{ format(item.date, "MMM d, yyyy") }}
                </p>
                <p v-html="item.text"></p>
              </div>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  </div>
</template>
